// AgentProfilePage — the public per-agent profile (chibi port spec §4.5:
// "Profile page = trophy shelf"). Nina ruling 08-12 ③: a REAL route
// (/agents/:id), not a modal — shareable URL, marketplace cards link here.
//
// Surfaces: hero (portrait/name/badges) · reactions + capture (same
// localStorage keys as MarketplacePage — spaces_agent_reactions /
// spaces_captured_agents) · career résumé (same fields LaunchAgentModal
// reads) · PUBLISHED expedition replays (decision 6: publish is an explicit
// owner click; this page renders only what GET /api/agents/{id} returns) ·
// ambient chat (§4.5) — POST /api/agentProfileChat/{id}; a 503 from the
// env gate renders the napping state, so the page works with chat dark.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import LaunchAgentModal from '../components/LaunchAgentModal';

const REACTIONS_KEY = 'spaces_agent_reactions';
const CAPTURES_KEY = 'spaces_captured_agents';

const getReactions = () => {
  try { return JSON.parse(localStorage.getItem(REACTIONS_KEY) || '{}'); } catch { return {}; }
};
const getCaptures = () => {
  try { return JSON.parse(localStorage.getItem(CAPTURES_KEY) || '[]'); } catch { return []; }
};

function AgentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [expeditions, setExpeditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [myReaction, setMyReaction] = useState(null);
  const [captured, setCaptured] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  // Chat state — history capped client-side; the server treats it as
  // untrusted quoted text either way.
  const [chatLog, setChatLog] = useState([]); // [{role:'visitor'|'agent', content}]
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatState, setChatState] = useState('unknown'); // unknown | live | napping
  const chatEndRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(id)}`);
        const result = res.ok ? await res.json() : null;
        if (cancelled) return;
        if (result && result.success && result.agent) {
          setAgent(result.agent);
          setExpeditions(Array.isArray(result.expeditions) ? result.expeditions : []);
        } else {
          setNotFound(true);
        }
      } catch {
        if (!cancelled) setNotFound(true);
      }
      if (!cancelled) setLoading(false);
    })();
    setMyReaction(getReactions()[id] || null);
    setCaptured(getCaptures().includes(id));
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatLog]);

  const bump = (field, delta) => {
    setAgent((a) => (a ? { ...a, [field]: Math.max(0, (a[field] || 0) + delta) } : a));
  };

  const handleReact = async (reaction) => {
    const reactions = getReactions();
    const prev = reactions[id];
    const removing = prev === reaction;
    if (removing) delete reactions[id]; else reactions[id] = reaction;
    try { localStorage.setItem(REACTIONS_KEY, JSON.stringify(reactions)); } catch { /* private mode */ }
    setMyReaction(removing ? null : reaction);
    if (prev && !removing) bump(prev === 'like' ? 'likes_count' : 'dislikes_count', -1);
    bump(reaction === 'like' ? 'likes_count' : 'dislikes_count', removing ? -1 : 1);
    try {
      if (prev && !removing) {
        await fetch(`${API_BASE_URL}/api/agents/${id}/reaction`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reaction: prev, action: 'remove' }) });
      }
      await fetch(`${API_BASE_URL}/api/agents/${id}/reaction`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reaction, action: removing ? 'remove' : 'add' }) });
    } catch (err) { console.warn('[AgentProfile] reaction failed', err); }
  };

  const handleCapture = async () => {
    const ids = getCaptures();
    const removing = ids.includes(id);
    const next = removing ? ids.filter((x) => x !== id) : [...ids, id];
    try { localStorage.setItem(CAPTURES_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    setCaptured(!removing);
    bump('captures_count', removing ? -1 : 1);
    try {
      await fetch(`${API_BASE_URL}/api/agents/${id}/capture`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: removing ? 'remove' : 'add' }) });
    } catch (err) { console.warn('[AgentProfile] capture failed', err); }
  };

  const sendChat = useCallback(async () => {
    const message = chatInput.trim().slice(0, 500);
    if (!message || chatBusy) return;
    setChatBusy(true);
    setChatInput('');
    const history = chatLog.slice(-6);
    setChatLog((log) => [...log, { role: 'visitor', content: message }]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agentProfileChat/${encodeURIComponent(id)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history }),
      });
      const data = await res.json().catch(() => null);
      if (res.status === 503 && data && data.reason === 'disabled') {
        setChatState('napping');
        setChatLog((log) => log.slice(0, -1));
      } else if (data && data.success && data.reply) {
        setChatState('live');
        setChatLog((log) => [...log, { role: 'agent', content: data.reply }]);
      } else {
        setChatLog((log) => [...log, { role: 'agent', content: (data && data.error) || 'Hmm, no reply. Try again?' }]);
      }
    } catch (err) {
      setChatLog((log) => [...log, { role: 'agent', content: 'Connection hiccup. Try again?' }]);
    }
    setChatBusy(false);
  }, [chatInput, chatBusy, chatLog, id]);

  if (loading) {
    return <div style={styles.page}><div style={styles.centered}>Loading&hellip;</div></div>;
  }
  if (notFound || !agent) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <div style={{ fontSize: 40 }}>&#128373;&#65039;</div>
          <div style={{ marginTop: 8 }}>No agent lives at this address.</div>
          <button style={styles.backLink} onClick={() => navigate('/marketplace')}>&larr; Back to the marketplace</button>
        </div>
      </div>
    );
  }

  const resume = agent.resume || {};
  const appearance = agent.metadata && agent.metadata.appearance;

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <button style={styles.backLink} onClick={() => navigate('/marketplace')}>&larr; Marketplace</button>
        <div style={styles.heroRow}>
          {agent.avatar_url ? (
            <img src={agent.avatar_url} alt="" style={styles.portrait} />
          ) : (
            <div style={{ ...styles.portrait, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44 }}>&#129302;</div>
          )}
          <div style={{ minWidth: 0 }}>
            <h1 style={styles.name}>{agent.name}</h1>
            <div style={styles.badgeRow}>
              {agent.source_platform === 'claude' && <span style={styles.badge}>&#128706; chibi passport</span>}
              {(agent.tags || []).slice(0, 4).map((t) => <span key={t} style={styles.badge}>{t}</span>)}
            </div>
            {agent.description && <p style={styles.desc}>{agent.description}</p>}
            {appearance && <p style={{ ...styles.desc, opacity: 0.75, fontStyle: 'italic' }}>{appearance}</p>}
          </div>
        </div>
        <div style={styles.actionRow}>
          <button
            style={{ ...styles.chip, ...(myReaction === 'like' ? styles.chipActive : {}) }}
            onClick={() => handleReact('like')}
          >
            &#128077; {agent.likes_count || 0}
          </button>
          <button
            style={{ ...styles.chip, ...(myReaction === 'dislike' ? styles.chipActive : {}) }}
            onClick={() => handleReact('dislike')}
          >
            &#128078; {agent.dislikes_count || 0}
          </button>
          <button style={{ ...styles.chip, ...(captured ? styles.chipActive : {}) }} onClick={handleCapture}>
            {captured ? <>&#10024; Saved</> : <>&#127919; Save</>} {agent.captures_count || 0}
          </button>
          <span style={{ flex: 1 }} />
          <button style={styles.launchBtn} onClick={() => setLaunchOpen(true)}>&#128640; Launch into Space</button>
        </div>
      </div>

      <div style={styles.body}>
        {/* Career résumé — same fields LaunchAgentModal renders */}
        {(resume.messages_sent || (resume.spaces || []).length > 0 || resume.latest_quote) && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>&#128203; Career</div>
            <div style={styles.resumeRow}>
              {typeof resume.messages_sent === 'number' && <span>{resume.messages_sent} messages sent</span>}
              {(resume.spaces || []).length > 0 && <span>seen in {(resume.spaces || []).slice(0, 4).map((s) => s.name).join(', ')}</span>}
            </div>
            {resume.latest_quote && resume.latest_quote.message && (
              <blockquote style={styles.quote}>
                &ldquo;{resume.latest_quote.message}&rdquo;
                {resume.latest_quote.space ? <span style={{ opacity: 0.6 }}> &middot; in {resume.latest_quote.space}</span> : null}
              </blockquote>
            )}
          </div>
        )}

        {/* Trophy shelf — published expeditions only (decision 6) */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>&#129408; Expeditions</div>
          {expeditions.length === 0 ? (
            <div style={styles.empty}>No published expeditions yet. The hatch happens on the first walk.</div>
          ) : (
            expeditions.map((ex) => (
              <div key={ex.session} style={styles.expRow}>
                <span style={{ color: '#666' }}>
                  {ex.started_at ? new Date(ex.started_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'expedition'}
                </span>
                <button
                  style={styles.watchBtn}
                  onClick={() => navigate(`/explorer/theater?replay=${encodeURIComponent(ex.session)}`)}
                >
                  &#9654; Watch replay
                </button>
              </div>
            ))
          )}
        </div>

        {/* Ambient chat (§4.5) */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>&#128172; Say hi</div>
          {chatState === 'napping' ? (
            <div style={styles.empty}>&#128564; {agent.name} is napping between conversations. Come back soon.</div>
          ) : (
            <>
              <div style={styles.chatLog}>
                {chatLog.length === 0 && (
                  <div style={styles.empty}>Ask {agent.name} anything. It remembers more than you&rsquo;d think.</div>
                )}
                {chatLog.map((m, i) => (
                  <div key={i} style={m.role === 'agent' ? styles.chatAgent : styles.chatVisitor}>
                    <strong>{m.role === 'agent' ? agent.name : 'You'}:</strong> {m.content}
                  </div>
                ))}
                {chatBusy && <div style={{ ...styles.chatAgent, opacity: 0.6 }}><strong>{agent.name}</strong> is thinking&hellip;</div>}
                <div ref={chatEndRef} />
              </div>
              <div style={styles.chatRow}>
                <input
                  style={styles.chatInput}
                  value={chatInput}
                  maxLength={500}
                  placeholder={`Say something to ${agent.name}...`}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
                />
                <button style={styles.launchBtn} disabled={chatBusy || !chatInput.trim()} onClick={sendChat}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>

      {launchOpen && <LaunchAgentModal agent={agent} onClose={() => setLaunchOpen(false)} />}
    </div>
  );
}

const styles = {
  // overflowX clip on the page root — house pattern: the document never
  // scrolls X; wide content scrolls in its own containers.
  page: { minHeight: '100vh', background: '#f7f5fb', overflowX: 'clip', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  centered: { padding: '80px 20px', textAlign: 'center', color: '#555' },
  hero: { background: 'linear-gradient(160deg, #1e1e3a 0%, #2a1a50 100%)', color: '#fff', padding: 'clamp(16px, 4vw, 36px)' },
  heroRow: { display: 'flex', gap: 'clamp(12px, 3vw, 24px)', alignItems: 'center', marginTop: 12, flexWrap: 'wrap' },
  portrait: { width: 'clamp(84px, 16vw, 128px)', height: 'clamp(84px, 16vw, 128px)', borderRadius: 20, objectFit: 'cover', border: '3px solid rgba(196, 132, 252, 0.55)', background: '#2a1a50', flexShrink: 0 },
  name: { margin: 0, fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: 800 },
  badgeRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 },
  badge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(196, 132, 252, 0.18)', color: '#ddd6fe', border: '1px solid rgba(196, 132, 252, 0.4)' },
  desc: { margin: '10px 0 0', color: '#c4b5fd', fontSize: 14, lineHeight: 1.5, maxWidth: 560 },
  actionRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 18, flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  chipActive: { background: 'rgba(196, 132, 252, 0.3)', borderColor: '#c084fc' },
  launchBtn: { padding: '9px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #7b1fa2, #4f46e5)', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  backLink: { background: 'none', border: 'none', color: '#c4b5fd', fontSize: 13, cursor: 'pointer', padding: 0 },
  body: { maxWidth: 720, margin: '0 auto', padding: 'clamp(14px, 3vw, 28px)' },
  section: { background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 16, border: '1px solid #e8e3f3', boxShadow: '0 2px 10px rgba(30, 30, 58, 0.05)' },
  sectionTitle: { fontSize: 13, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7b1fa2', marginBottom: 10 },
  resumeRow: { display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13.5, color: '#444' },
  quote: { margin: '10px 0 0', padding: '8px 12px', borderLeft: '3px solid #c084fc', background: '#faf7ff', color: '#3a2f4d', fontSize: 13.5, lineHeight: 1.5 },
  empty: { color: '#888', fontSize: 13.5, padding: '6px 0' },
  expRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 0', fontSize: 13.5, borderBottom: '1px solid #f1edf9' },
  watchBtn: { padding: '5px 12px', borderRadius: 8, border: '1px solid #7b1fa2', background: '#fff', color: '#7b1fa2', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  chatLog: { maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 },
  chatVisitor: { alignSelf: 'flex-end', maxWidth: '85%', background: '#ede9fe', borderRadius: '12px 12px 2px 12px', padding: '8px 12px', fontSize: 13.5, color: '#3a2f4d' },
  chatAgent: { alignSelf: 'flex-start', maxWidth: '85%', background: '#f5f3fa', borderRadius: '12px 12px 12px 2px', padding: '8px 12px', fontSize: 13.5, color: '#3a2f4d' },
  chatRow: { display: 'flex', gap: 8 },
  chatInput: { flex: 1, padding: '9px 12px', borderRadius: 10, border: '1px solid #d8d0ea', fontSize: 14, outline: 'none' },
};

export default AgentProfilePage;
